import { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { getData } from "country-list";
import SimpleBarReact from "simplebar-react";
import classNames from "classnames";
import { Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import countriesLib from "i18n-iso-countries";

import FormikInput from "../FormikInput";
import SearchInput from "../SearchInput";
import DropdownItem from "../DropdownItem";
import { sortByKey, preventEnterSubmit } from "@helpers/common";

import styles from "./index.module.scss";

import enLocale from "i18n-iso-countries/langs/en.json";
import esLocale from "i18n-iso-countries/langs/es.json";

countriesLib.registerLocale(enLocale);
countriesLib.registerLocale(esLocale);

const sortedCountries = sortByKey(getData(), "name");

const Placeholder = ({ children }) => (
  <div
    className={classNames(
      styles.placeholder,
      "text-body-1 text-regular text-secondary text-center",
    )}
  >
    {children}
  </div>
);

const FormikCountryInput = ({
  shouldSetCountryId = false,
  flip = false,
  countryList = sortedCountries,
  label = "STATE_OR_PROVINCE",
  placeholder = "COUNTRY",
  wrapperClassName,
  name = "countryName",
  isPayout,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation("placeholders");
  const [countries, setCountries] = useState(countryList);
  const [searchValue, setSearchValue] = useState("");
  const { values, setFieldValue } = useFormikContext();

  const toggle = () => setIsOpen((state) => !state);

  const handleSelect = (value) => {
    if (isPayout) {
      setFieldValue("countryAlpha3", value.threeLetterCode);
      setFieldValue("countryName", value.name);
    } else {
      setFieldValue("countryCode", value.code);
      setFieldValue(
        name,
        value.countryName || value.country || value.name,
        true,
      );

      if (shouldSetCountryId) {
        setFieldValue("countryId", value.id, true);
      }
    }
  };

  const handleSearchChange = ({ target }) => setSearchValue(target.value);
  const handleOnResetClick = () => setSearchValue("");

  useEffect(() => {
    setCountries(
      countryList
        .map(({ code, name, ...country }) => ({
          ...country,
          code,
          name: countriesLib.getName(code, i18n.language ?? "en") || name,
        }))
        .filter(({ name, country, code }) => {
          const countryName = name || country;

          return countryName
            .toLocaleLowerCase()
            .startsWith(searchValue.toLocaleLowerCase());
        }),
    );
  }, [countryList, searchValue, i18n.language]);

  return (
    <Dropdown
      isOpen={isOpen}
      toggle={toggle}
      className={classNames(styles.dropdown, wrapperClassName)}
    >
      <DropdownToggle
        caret
        className={classNames(
          "btn-resetStyle text-start with-label",
          styles.dropdownToogle,
        )}
      >
        <FormikInput
          label={label}
          placeholder={placeholder}
          name={name}
          wrapperClassName="pe-none"
          readOnly
        />
      </DropdownToggle>
      <DropdownMenu flip={flip} className="w-100 dropdown-menu-input">
        <SimpleBarReact className={styles.scroll}>
          <div className="d-flex w-100 px-3 mb-3">
            <SearchInput
              inputValue={searchValue}
              onChange={handleSearchChange}
              wrapperClassName="w-100 m-0 bg-transparent"
              placeholder="SEARCH"
              onKeyDown={preventEnterSubmit}
              withClearButton
              onResetClick={handleOnResetClick}
              bordered
            />
          </div>
          {!countries.length && <Placeholder>{t("NO_MATCHES")}</Placeholder>}
          {countries.map(({ name, threeLetterCode, id, country, code }) => (
            <DropdownItem
              key={code}
              name={isPayout ? name : name || country}
              isSelected={
                isPayout
                  ? values.countryAlpha3 === threeLetterCode
                  : values.countryCode === code
              }
              onClick={() =>
                isPayout
                  ? handleSelect({ threeLetterCode, name, country })
                  : handleSelect({ code, name, country, id })
              }
            />
          ))}
        </SimpleBarReact>
      </DropdownMenu>
    </Dropdown>
  );
};

export default FormikCountryInput;
